import React from "react";
import { useAuth } from "../auth/AuthContext";
import useRole from "../auth/useRole";
import useStore from "../store/useStore";
import { ACCOUNT_TYPES } from "../services/profileService";
import { subscribeBusinessById, subscribeBusinessMembershipForUser } from "../services/businessService";

export default function useActiveBusinessMembership() {
  const { user } = useAuth() || {};
  const { isAdmin } = useRole();
  const { userProfile } = useStore();

  const accountType = userProfile?.accountType;
  const isBusinessUser = accountType === ACCOUNT_TYPES.BUSINESS;
  const activeBusinessId = String(userProfile?.primaryBusinessId || "").trim() || null;
  const hasActiveBusinessContext = Boolean(activeBusinessId);

  const [membership, setMembership] = React.useState(null);
  const [membershipLoading, setMembershipLoading] = React.useState(false);
  const [isActiveBusinessProfileLive, setIsActiveBusinessProfileLive] = React.useState(true);

  React.useEffect(() => {
    if (isAdmin || !user?.uid || !activeBusinessId) {
      setMembership(null);
      setMembershipLoading(false);
      return () => {};
    }

    setMembershipLoading(true);
    const unsub = subscribeBusinessMembershipForUser(
      { businessId: activeBusinessId, uid: user.uid },
      (nextMembership) => {
        setMembership(nextMembership || null);
        setMembershipLoading(false);
      },
      (error) => {
        // Not cosmetic: membership drives canAccessCollections, so a failure
        // here hides the collection-scoped nav (Playlists, Inssa Live) for the
        // rest of the session — this listener is never re-established.
        console.warn(
          "[Membership] Subscription failed; collection-scoped nav stays hidden until reload:",
          error?.code || "(no code)",
          error,
        );
        setMembership(null);
        setMembershipLoading(false);
      }
    );

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [isAdmin, user?.uid, activeBusinessId]);

  // Subscribed, not read once: activating a draft business flips its status
  // without changing activeBusinessId, and a one-shot read left the
  // collection-scoped nav hidden until the user refreshed the page.
  React.useEffect(() => {
    if (!activeBusinessId) {
      setIsActiveBusinessProfileLive(true);
      return () => {};
    }

    const unsub = subscribeBusinessById(
      activeBusinessId,
      (business) => {
        const status = String(business?.status || "").trim().toLowerCase();
        const live = !status || status === "active";
        // A non-active status here is the ONLY branch that forces
        // canAccessCollections false for a business owner, hiding Playlists and
        // Inssa Live. If the nav is missing, this is what to read.
        console.info("[Membership] Active business status", {
          activeBusinessId,
          status: status || "(none)",
          isActiveBusinessProfileLive: live,
        });
        setIsActiveBusinessProfileLive(live);
      },
      (error) => {
        // Fail open so a read error never locks an owner out of their own tools.
        console.warn(
          "[Membership] Business status subscription failed; failing open:",
          error?.code || "(no code)",
          error,
        );
        setIsActiveBusinessProfileLive(true);
      },
    );

    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [activeBusinessId]);

  const membershipRole = String(membership?.role || "").trim().toLowerCase() || null;
  const isEmployee = membershipRole === "collaborator";
  const isOwner = membershipRole === "owner";
  const employeeScopes = membership?.scopes || {};
  const hasMembership = Boolean(
    membership?.id ||
      membershipRole ||
      membership?.uid ||
      membership?.businessId
  );
  const hasExplicitScopeSettings =
    Object.prototype.hasOwnProperty.call(employeeScopes, "tv") ||
    Object.prototype.hasOwnProperty.call(employeeScopes, "timeCapsules");

  const defaultScopesForRole = (() => {
    switch (membershipRole) {
      case "owner":
      case "manager":
      case "collaborator":
        return { tv: true, timeCapsules: true };
      case "tv_manager":
        return { tv: true, timeCapsules: false };
      case "timecapsules_manager":
        return { tv: false, timeCapsules: true };
      default:
        return { tv: true, timeCapsules: true };
    }
  })();
  const resolvedMemberScopes = hasExplicitScopeSettings
    ? {
        tv: Boolean(employeeScopes?.tv),
        timeCapsules: Boolean(employeeScopes?.timeCapsules),
      }
    : defaultScopesForRole;

  // Real-time scoped permissions for active business context.
  let canAccessCollections = false;
  let canAccessTimeCapsules = false;
  if (isAdmin) {
    canAccessCollections = true;
    canAccessTimeCapsules = true;
  } else if (hasActiveBusinessContext && !isActiveBusinessProfileLive) {
    // Draft/inactive/deleted business contexts should not grant business-scoped content operations.
    canAccessCollections = false;
    canAccessTimeCapsules = false;
  } else if (!hasActiveBusinessContext) {
    canAccessCollections = isBusinessUser;
    canAccessTimeCapsules = isBusinessUser;
  } else if (isOwner) {
    canAccessCollections = true;
    canAccessTimeCapsules = true;
  } else if (hasMembership) {
    canAccessCollections = Boolean(resolvedMemberScopes?.tv);
    canAccessTimeCapsules = Boolean(resolvedMemberScopes?.timeCapsules);
  } else if (isBusinessUser) {
    // Do not hard-lock business owners when membership docs are delayed/missing.
    canAccessCollections = true;
    canAccessTimeCapsules = true;
  } else if (accountType === ACCOUNT_TYPES.INDIVIDUAL) {
    // Invited members can remain individual while operating inside an active business context.
    canAccessCollections = true;
    canAccessTimeCapsules = true;
  }

  return {
    membership,
    membershipRole,
    isEmployee,
    isOwner,
    membershipLoading,
    hasActiveBusinessContext,
    isActiveBusinessProfileLive,
    canAccessCollections,
    canAccessTimeCapsules,
  };
}
